
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Build from 'plaid-threads/Icons/Build';
import Key from 'plaid-threads/Icons/Key';
import Mobile from 'plaid-threads/Icons/Mobile';
import Rocket from 'plaid-threads/Icons/Rocket';
import Sandbox from 'plaid-threads/Icons/Sandbox';
import Transfer from 'plaid-threads/Icons/Transfer';
import Actions from '../../../../components/docs/Actions';
import Layout from '../../../../components/docs/Layout';
import Image from '../../../../components/docs/Image';
import Video from '../../../../components/accelerate/Video';
import Bio from '../../../../components/accelerate/Bio';
export const metadata = {
  noIndex: true,
  title: 'Program overview',
  section: 'Accelerate',
  toc: false,
  description: 'The resources available.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    {/* Accelerate Components */}


    <Header title='Program overview' subtitle='The resources available to you.' mdxType="Header" />
    <p>{`Welcome to Accelerate, we are so glad you are able to join us as part of the inaugural class of Accelerate and we can’t wait to embark on this startup growth journey with you!`}</p>
    <Layout.Spacer units={4} />
    <h6 {...{
      "id": "why-did-we-create-accelerate"
    }}>{`Why did we create Accelerate?`}</h6>
    <p>{`Entrepreneurship should be accessible. Anyone with a great idea should have access to the same opportunities and resources to be successful. Plaid believes in technology as a force for good. We want to unlock financial freedom for everyone by empowering entrepreneurs and innovators. To do that, we hope to achieve more inclusivity and equitable access to financial services for everyone.`}</p>
    <p>{`We know that there is still a significant disparity in access, especially at the earliest and most crucial stages of company development. Our goal is to help power a financial system that works for everyone, and we recognize that one way to further that goal is by supporting and promoting a diverse ecosystem of entrepreneurs.`}</p>
    <Layout.Spacer units={5} />
    <h6 {...{
      "id": "focus-areas"
    }}>{`Focus areas`}</h6>
    <p>{`We designed Accelerate to support early-stage BIPOC founders by offering resources to launch businesses with 3 core focus areas:`}</p>
    <ol>
      <li parentName="ol">{`Providing resources to accelerate growth`}</li>
      <li parentName="ol">{`Making it easier to access capital and services`}</li>
      <li parentName="ol">{`Offering ongoing mentorship and support`}</li>
    </ol>
    <Layout.Spacer units={5} />
    <h4 {...{
      "id": "providing-resources-to-accelerate-growth"
    }}>{`Providing resources to accelerate growth`}</h4>
    <p>{`To kick off the program, founders will participate in a 3 day virtual bootcamp with workshops on relevant technical, product, and business topics to help guide early business development. Topics will be led by internal experts with external input. Each workshop will be tailored to founders’ specific needs based on a survey sent prior to the program kick-off date. Topics will include:`}</p>
    <ul>
      <li parentName="ul">{`Fundraising and engaging investors`}</li>
      <li parentName="ul">{`Marketing and communications strategy`}</li>
      <li parentName="ul">{`Information security best practices`}</li>
      <li parentName="ul">{`Engineering`}</li>
      <li parentName="ul">{`Brand, and designing user centric product experiences`}</li>
      <li parentName="ul">{`Policy and engaging with regulators`}</li>
      <li parentName="ul">{`Hiring and recruiting`}</li>
      <li parentName="ul">{`Financial management`}</li>
      <li parentName="ul">{`Ongoing mentorship`}</li>
    </ul>
    <p>{`Each workshop will cover an introduction to that function, followed by a deep-dive into functional aspects that will help founders address their current challenges. The workshops will also include a hands-on exercise or activity to help you practice and implement suggested strategies and finally actionable insights and material that will help guide founders build and grow these functions after the workshop.`}</p>
    <p>{`To request other topics that will be impactful for founders, please reach out to us with your ideas at `}<inlineCode parentName="p">{`finrise@plaid.com`}</inlineCode></p>
    <Layout.Spacer units={4} />
    <h4 {...{
      "id": "making-it-easier-access-to-capital-and-services"
    }}>{`Making it easier access to capital and services`}</h4>
    <p>{`Access to resources & networking opportunities are critical during the earliest stages of company building. Through Plaid’s partnership with a network of purpose-built venture capital firms, network service providers, and accelerators, startups in our program will have access to networking opportunities, discounted services and ad credits, and pitch practice.`}</p>
    <Image src='/assets/img/accelerate/2021/Accelerate-capital-partners@2x.png' alt='Our 2021 partners.' isLazy={false} mdxType="Image" />
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Capital`}</strong>{` - Plaid has built a network of capital partners eager to engage with and support Accelerate participants. Partners will contribute to a workshop on engaging investors and fundraising in the 3 day bootcamp. Following the bootcamp, founders will participate in an investor pitch and feedback session and a separate Q&A and networking event. This element of the program will provide founders with both targeted guidance on raising capital and opportunities to get to know and network with funds relevant in the space.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Services`}</strong>{` - When building a company, it’s important to select vendors and service partners that are stage appropriate, understand your use case, and are supportive partners as you grow your business. Plaid has put identified fintech startup-friendly service providers to streamline the process of selecting vendors to partner with. Additionally some providers offer special rates and discounts specifically for Accelerate participants.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Reduced Rates`}</strong>{` - Accelerate participants will be able to access the Plaid API at no cost throughout the duration of the 9-month program. Following the program, participants can continue using Plaid services for 3 years at discounted rates.`}</p>
      </li>
    </ul>
    <Layout.Spacer units={2} />
    <h4 {...{
      "id": "ongoing-support"
    }}>{`Ongoing support`}</h4>
    <p>{`Following the bootcamp, participants will have access to ongoing support through a nine-month period including:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Dedicated Account Manager`}</strong>{` - A day-to-day contact from Plaid who provides product best practices and helps them develop their business`}</li>
      <li parentName="ul"><strong parentName="li">{`Skillshare Network`}</strong>{` - An internal network that founders can leverage for ongoing guidance on topics covered in the bootcamp (e.g., navigating the policy and regulatory landscapes)`}</li>
      <li parentName="ul"><strong parentName="li">{`Mentorship from Plaid leaders`}</strong>{` - Founders in the program will be paired with Plaid leaders who can offer guidance on the early stages of company building`}</li>
      <li parentName="ul"><strong parentName="li">{`Community`}</strong>{` - Spaces to network and learn from other entrepreneurs in the program with the opportunity to mentor future classes.`}</li>
    </ul>
    <p>{`Read more about your ongoing support network `}<a parentName="p" {...{
        "href": "/accelerate/2021/ongoing-support"
      }}>{`here`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "providing-resources-to-accelerate-growth",
        level: 4,
        title: "Providing resources to accelerate growth",
      },
{
        id: "making-it-easier-access-to-capital-and-services",
        level: 4,
        title: "Making it easier access to capital and services",
      },
{
        id: "ongoing-support",
        level: 4,
        title: "Ongoing support",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
